import React, { useState, useEffect, useMemo } from "react";
import { message, Alert, Checkbox, InputNumber, Statistic, Row, Col, Typography } from "antd";
import CriticalActionModal from "./CriticalActionModal";
import { modifyPaidFeeDetail } from "../api/subscriptions/subscriptionsRequest";

const { Text } = Typography;

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)} DH`;

const feeLabel = (t) =>
  ({ registration_fee: "Frais d'inscription", reactivation_fee: "Frais de réactivation" }[t] || t);

/**
 * Correct a PAID registration_fee / reactivation_fee detail line.
 * Δ = new − old cascades to the payment total, transaction and the agent's balance.
 */
const ModifyPaidFeeModal = ({ visible, onCancel, onSuccess, detail, agentBalance = null }) => {
  const [loading, setLoading] = useState(false);
  const [newAmount, setNewAmount] = useState(0);
  const [updateAgentSolde, setUpdateAgentSolde] = useState(true);

  const oldAmount = Number(detail?.amount ?? 0);

  useEffect(() => {
    if (visible && detail) {
      setNewAmount(Number(detail.amount ?? 0));
      setUpdateAgentSolde(true);
    }
  }, [visible, detail]);

  const delta = useMemo(() => {
    const n = Number(newAmount);
    return isNaN(n) ? 0 : Number((n - oldAmount).toFixed(2));
  }, [newAmount, oldAmount]);

  const amountValid =
    newAmount !== null && newAmount !== undefined && !isNaN(Number(newAmount)) && Number(newAmount) >= 0;

  const hasBalance = agentBalance !== null && agentBalance !== undefined && !isNaN(Number(agentBalance));
  const projectedBalance = useMemo(() => {
    if (!hasBalance || !updateAgentSolde) return hasBalance ? Number(agentBalance) : null;
    return Number((Number(agentBalance) + delta).toFixed(2));
  }, [hasBalance, agentBalance, delta, updateAgentSolde]);

  const willGoNegative = updateAgentSolde && projectedBalance !== null && projectedBalance < 0;

  const handleConfirm = async (reason) => {
    if (!detail) return;
    setLoading(true);
    try {
      const response = await modifyPaidFeeDetail(detail.id, {
        new_amount: Number(newAmount),
        reason,
        update_agent_solde: updateAgentSolde,
      });
      if (response?.success) {
        message.success(response.message || "Frais corrigé avec succès");
        onSuccess?.(response.data);
      } else if (response?.message === "Aucun changement détecté") {
        message.info("Aucun changement détecté");
        onCancel?.();
      } else {
        message.error(response?.message || response?.error || "Erreur lors de la correction");
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de la correction du frais");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CriticalActionModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      loading={loading}
      title={`Corriger : ${feeLabel(detail?.type)}`}
      okText="Appliquer la correction"
      extraValid={amountValid}
      summaryItems={[
        { label: "Type", to: feeLabel(detail?.type) },
        { label: "Montant", from: fmt(oldAmount), to: fmt(Number(newAmount)) },
        { label: "Différence (Δ)", to: `${delta > 0 ? "+" : ""}${delta.toFixed(2)} DH` },
      ]}
      warningBanner={
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Correction d'un frais déjà payé"
            description="Le montant sera répercuté sur le paiement, la transaction et le solde de l'agent collecteur."
          />
          {willGoNegative && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message="Attention : solde agent négatif"
              description={`Cette action laissera l'agent avec un solde négatif de ${fmt(projectedBalance)}.`}
            />
          )}
        </>
      }
    >
      <div style={{ marginTop: 12 }}>
        <Text strong>Nouveau montant</Text>
        <InputNumber
          style={{ width: "100%", marginTop: 6 }}
          value={newAmount}
          onChange={setNewAmount}
          min={0}
          step={0.01}
          precision={2}
          status={!amountValid ? "error" : ""}
          addonAfter="DH"
        />

        <Checkbox
          checked={updateAgentSolde}
          onChange={(e) => setUpdateAgentSolde(e.target.checked)}
          style={{ marginTop: 12 }}
        >
          Ajuster le solde de l'agent collecteur ({delta >= 0 ? "+" : ""}
          {delta.toFixed(2)} DH)
        </Checkbox>

        {hasBalance && (
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Solde actuel de l'agent" value={Number(agentBalance)} precision={2} suffix="DH" />
            </Col>
            <Col span={12}>
              <Statistic
                title="Solde après l'action"
                value={projectedBalance ?? Number(agentBalance)}
                precision={2}
                suffix="DH"
                valueStyle={{ color: willGoNegative ? "#cf1322" : undefined }}
              />
            </Col>
          </Row>
        )}
      </div>
    </CriticalActionModal>
  );
};

export default ModifyPaidFeeModal;
